import * as Plotly from 'plotly.js-dist-min';
import { ElementRef, Injectable } from '@angular/core';
import { SnackbarService } from '../snackbar.service';

@Injectable({ providedIn: 'root' })
export class PlotlyService {
  private static readonly DEFAULT_COLOR = '#1F273C';
  private static readonly DASHED_COLOR = '#ADA29A';
  private static readonly LINE_WIDTH = 1.5;

  myPlot!: ElementRef;
  private readonly COLORS = ['#1F273C', '#999BBB', '#ADA29A', '#9ABEB4', '#C997A5', '#C6BF8D', '#93B6C1', '#C4C4C4'];

  constructor(private snackbarService: SnackbarService) {}

  renderChart(
    chartId: string,
    traces: Partial<Plotly.PlotData>[],
    layout: Partial<Plotly.Layout>,
    config: Partial<Plotly.Config>
  ): Promise<Plotly.PlotlyHTMLElement> {
    return Plotly.newPlot(chartId, traces, layout, config);
  }

  private filterDataPoints(
    data: { date: string; value: number }[],
    start: string,
    end: string
  ): {
    before: { date: string; value: number }[];
    during: { date: string; value: number }[];
    after: { date: string; value: number }[];
  } {
    console.log('simulationsdata', data, start, end);
    return {
      before: data.filter(point => new Date(point.date) < new Date(start)),
      during: data.filter(
        point => new Date(point.date) >= new Date(start) && new Date(point.date) <= new Date(end)
      ),
      after: data.filter(point => new Date(point.date) >= new Date(end))
    };
  }

  private createTrace(
    data: { date: string; value: number }[],
    chartType: Plotly.PlotType,
    color: string,
    dash: 'solid' | 'dot',
    name?: string,
    showLegend: boolean = false
  ): Partial<Plotly.PlotData> {
    return {
      x: data.map(point => point.date.split('T')[0]),
      y: data.map(point => point.value || 0),
      type: chartType,
      name,
      showlegend: showLegend,
      line: { color, dash, width: PlotlyService.LINE_WIDTH },
      mode: 'lines'
    };
  }

  /**
   * Erzeugt Traces und zusätzlich Arrays mit Shapes und Annotations für vertikale Linien.
   * Die Übergangsposition wird aus dem tatsächlichen letzten Punkt des "during"-Segments ermittelt.
   */
  createTraces(
    chartData: {
      data: { date: string; value: number }[];
      name: string;
      isBenchmark: boolean;
    }[],
    chartType: Plotly.PlotType,
    verticalLineDates?: {
      start_date: string;
      end_date: string;
      has_dashed_line: boolean;
      translations?: any;
    }[]
  ): {
    traces: Partial<Plotly.PlotData>[];
    verticalShapes: Partial<Plotly.Shape>[];
    verticalAnnotations: Partial<Plotly.Annotations>[];
  } {
    const traces: Partial<Plotly.PlotData>[] = [];
    const verticalShapes: Partial<Plotly.Shape>[] = [];
    const verticalAnnotations: Partial<Plotly.Annotations>[] = [];

    chartData.forEach((item, index) => {
      const color = this.COLORS[index] || PlotlyService.DEFAULT_COLOR;

      // -- 1) If item is a Benchmark: just one normal line, no dashed transitions --
      if (item.isBenchmark) {
        traces.push(
          this.createTrace(
            item.data,
            chartType,
            color,
            'solid',
            item.name,       // show name in legend
            true
          )
        );
        return; // done for this item
      }

      // -- 2) Product (isBenchmark === false) --
      // If no vertical lines or no need for dashed transitions, push one line
      if (!verticalLineDates?.length) {
        traces.push(
          this.createTrace(
            item.data,
            chartType,
            color,
            'solid',
            item.name,
            true
          )
        );
        return;
      }

      // If verticalLineDates exist, process them:
      verticalLineDates.forEach(({ start_date, end_date, has_dashed_line, translations }) => {
        // Only split if has_dashed_line = true
        if (has_dashed_line) {
          // Split data into before/during/after
          const { before, during, after } = this.filterDataPoints(
            item.data,
            start_date,
            end_date
          );

          // 1) "before" segment (solid)
          if (before.length) {
            traces.push(
              this.createTrace(
                before,
                chartType,
                color,
                'solid',
                item.name,
                true
              )
            );
          }

          // 2) "during" segment (dashed)
          if (during.length) {
            traces.push(
              this.createTrace(
                during,
                chartType,
                PlotlyService.DASHED_COLOR,
                'dot',  // dashed line
                undefined,
                false
              )
            );
          }

          // -- Eliminate gap by matching last "during" point in "after" --
          // If `during` and `after` are nonempty and their boundary doesn't match:
          let transitionDate = end_date; // fallback
          if (
            during.length &&
            after.length &&
            during[during.length - 1].date !== after[0].date
          ) {
            // Reuse the last during point so there is no gap
            transitionDate = during[during.length - 1].date;
            after.unshift(during[during.length - 1]);
          }

          // 3) "after" segment (back to solid)
          if (after.length) {
            traces.push(
              this.createTrace(
                after,
                chartType,
                color,
                'solid',
                undefined,
                false
              )
            );
          }

          // -- Vertical line & annotation at the "transitionDate" --
          verticalShapes.push(this.createVerticalLineShape(transitionDate));
          const annotationText =
            translations?.['en']?.title ?? 'Transition';
          verticalAnnotations.push(
            this.createLineAnnotation(transitionDate, annotationText)
          );

        } else {
          // has_dashed_line = false => one continuous line
          traces.push(
            this.createTrace(
              item.data,
              chartType,
              color,
              'solid',
              item.name,
              true
            )
          );
        }
      });
    });

    return { traces, verticalShapes, verticalAnnotations };
  }



  generateTracesForPieChart(item: any, isCustomData: boolean, statistic: any): Partial<Plotly.PlotData> {
    return {
      labels: isCustomData
        ? item.data.rows.map((point: any) => point[0])
        : item.data.map((point: any) => this.formatValue(point.date)),
      values: isCustomData
        ? item.data.rows.map((point: any) => parseFloat(point[1]))
        : item.data.map((point: any) => this.formatValue(point.value)),
      type: 'pie',
      name: item.name,
      hole: statistic === 'donut' ? 0.4 : 0,
      marker: { colors: this.COLORS }
    };
  }

  generateTracesForPieChartPdfForPages(
    item: any,
    itemRows: any,
    isCustomData: boolean,
    statistic: any
  ): Partial<Plotly.PlotData> {
    return {
      labels: isCustomData
        ? itemRows.map((point: any) => point[0])
        : itemRows.map((point: any) => this.formatValue(point.date)),
      values: isCustomData
        ? itemRows.map((point: any) => parseFloat(point[1]))
        : itemRows.map((point: any) => this.formatValue(point.value)),
      type: 'pie',
      name: item.name,
      hole: statistic === 'donut' ? 0.4 : 0,
      marker: { colors: this.COLORS }
    };
  }

  generateTracesFor3DScatterChart(item: any, index: number): Partial<Plotly.PlotData> {
    return {
      x: item.data.rows.map((point: any) => point[0]),
      y: item.data.rows.map((point: any) => parseFloat(point[1])),
      z: item.data.rows.map((point: any) => parseFloat(point[2])),
      mode: 'markers',
      type: 'scatter3d',
      name: item.name,
      marker: {
        color: this.COLORS[index % this.COLORS.length],
        opacity: 0.2,
        size: 5
      }
    };
  }

  generateTracesForTableChart(
    chartData: { data: { date: string; value: number }[]; name: string }[]
  ): Partial<Plotly.PlotData>[] {
    const dateSet = new Set<string>();
    chartData.forEach(item => {
      item.data.forEach(point => {
        dateSet.add(point.date.split('T')[0]);
      });
    });
    const sortedDates = Array.from(dateSet).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    const seriesData = chartData.map(item => {
      const map = new Map<string, number>();
      item.data.forEach(point => {
        map.set(point.date.split('T')[0], point.value);
      });
      const values = sortedDates.map(date => map.get(date) ?? '');
      return { name: item.name, values };
    });

    const headerValues = ['Date', ...seriesData.map(s => s.name)];
    const cellValues = [sortedDates, ...seriesData.map(s => s.values)];

    const tableTrace = {
      type: 'table',
      header: {
        values: headerValues,
        align: 'center',
        fill: { color: 'lightgrey' }
      },
      cells: {
        values: cellValues,
        align: 'center'
      }
    } as any;

    return [tableTrace];
  }

  generateTracesFor2DChartsBackup(
    chartType: Plotly.PlotType,
    rows: any[],
    isCustomData: boolean,
    chartDataInput: any,
    chart: any
  ): Partial<Plotly.PlotData>[] {
    const xAxis = rows.map((row: any) => row[0]);
    return chartDataInput.data.headers.slice(1).map((header: string, index: number) => {
      const yAxis = rows.map((row: any) => parseFloat(row[index + 1]));
      return {
        x: xAxis,
        y: yAxis,
        mode: chartType === 'scatter' ? 'lines' : undefined,
        type: chartType,
        name: header,
        marker: { color: this.COLORS[index % this.COLORS.length] },
        line:
          chartType === 'scatter' && chart === 'line'
            ? { color: this.COLORS[index % this.COLORS.length] }
            : undefined
      };
    });
  }

  createHorizontalBarTrace(
    data: any,
    color: string,
    name?: string,
    showLegend: boolean = false
  ): Partial<Plotly.PlotData> {
    if (data.length > 0 && Array.isArray(data[0])) {
      return {
        x: data.map((row: any[]) => parseFloat(row[1]) || 0),
        y: data.map((row: any[]) => row[0]),
        type: 'bar',
        orientation: 'h',
        name,
        showlegend: showLegend,
        marker: { color }
      };
    } else {
      return {
        x: data.map((point: { value: number }) => point.value || 0),
        y: data.map((point: { date: string }) => point.date.split('T')[0]),
        type: 'bar',
        orientation: 'h',
        name,
        showlegend: showLegend,
        marker: { color }
      };
    }
  }

  createHorizontalBarTraces(
    chartData: { data: any; name: string; isBenchmark?: boolean }[]
  ): Partial<Plotly.PlotData>[] {
    return chartData.map((item, index) => {
      const color = this.COLORS[index] || PlotlyService.DEFAULT_COLOR;
      let dataArray: { date: string; value: number }[];
      if (Array.isArray(item.data)) {
        dataArray = item.data;
      } else if (item.data && Array.isArray(item.data.rows)) {
        dataArray = item.data.rows;
      } else {
        dataArray = [];
      }
      return this.createHorizontalBarTrace(dataArray, color, item.name, index === 0);
    });
  }

  generateTracesFor2DCharts(
    chartType: Plotly.PlotType,
    rows: any[],
    isCustomData: boolean,
    chartDataInput: any,
    chart: any
  ): Partial<Plotly.PlotData>[] {
    const xAxis = rows.map((row: any) => row[0]);
    const headers: string[] = chartDataInput?.data?.headers ?? [];
    const numberOfColumns = rows.length > 0 ? rows[0].length - 1 : 0;
    let yAxisLabels: string[];
    if (headers.length > 1) {
      yAxisLabels = headers.slice(1);
    } else {
      yAxisLabels = Array.from({ length: numberOfColumns }, (_, i) => `Series ${i + 1}`);
    }
    return yAxisLabels.map((label: string, index: number) => {
      const yAxis = rows.map((row: any) => parseFloat(row[index + 1]) || 0);
      return {
        x: xAxis,
        y: yAxis,
        mode: chartType === 'scatter' ? 'lines' : undefined,
        type: chartType,
        name: label,
        marker: { color: this.COLORS[index % this.COLORS.length] },
        line:
          chartType === 'scatter' && chart === 'line'
            ? { color: this.COLORS[index % this.COLORS.length] }
            : undefined
      } as Partial<Plotly.PlotData>;
    });
  }

  generateTracesForDefault2DCharts(
    item: any,
    index: number,
    chartType: Plotly.PlotType,
    chart: any
  ): Partial<Plotly.PlotData> {
    return {
      x: item.data.map((point: any) => point.date.split('T')[0]),
      y: item.data.map((point: any) => point.value ?? 0),
      mode:
        chartType === 'scatter'
          ? chart === 'line'
            ? 'lines'
            : 'markers'
          : undefined,
      type: chartType,
      name: item.name,
      marker: { color: this.COLORS[index % this.COLORS.length] },
      line:
        chartType === 'scatter' && chart === 'line'
          ? { color: this.COLORS[index % this.COLORS.length] }
          : undefined,
      text: item.data.map((point: any) => (point.value === null ? 'N/A' : ''))
    };
  }

  private formatValue(value: any): any {
    return value !== null ? value : 'N/A';
  }

  getBaseLayout(
    isGroupedBarChart: boolean,
    verticalLineDates?: { start_date: string; end_date: string; translations?: any }[],
    xRange?: [string, string]
  ): Partial<Plotly.Layout> {
    const shapes: Partial<Plotly.Shape>[] = [];
    const annotations: Partial<Plotly.Annotations>[] = [];

    if (verticalLineDates && xRange) {
      verticalLineDates.forEach(({ end_date, translations }) => {
        const isInRange =
          new Date(end_date) >= new Date(xRange[0]) && new Date(end_date) <= new Date(xRange[1]);
        if (isInRange) {
          shapes.push(this.createVerticalLineShape(end_date));
          annotations.push(
            this.createLineAnnotation(
              end_date,
              translations && translations['en'] ? translations['en'].title : 'Transition'
            )
          );
        }
      });
    }

    return {
      barmode: isGroupedBarChart ? 'group' : undefined,
      showlegend: true,
      font: { family: 'Font', size: 10, color: '#1F273C' },
      hovermode: 'x unified',
      legend: { orientation: 'h', y: -0.1 },
      margin: { l: 28, r: 0, t: 0, b: 25 },
      yaxis: { fixedrange: true },
      dragmode: 'zoom',
      shapes,
      annotations
    };
  }

  getBaseLayoutCombined(
    isGroupedBarChart: boolean,
    verticalLineDates?: { start_date: string; end_date: string; translations?: any }[],
    xRange?: [string, string]
  ): Partial<Plotly.Layout> {
    const shapes: Partial<Plotly.Shape>[] = [];
    const annotations: Partial<Plotly.Annotations>[] = [];

    if (verticalLineDates && xRange) {
      verticalLineDates.forEach(({ end_date, translations }) => {
        const isInRange =
          new Date(end_date) >= new Date(xRange[0]) && new Date(end_date) <= new Date(xRange[1]);
        if (isInRange) {
          shapes.push(this.createVerticalLineShape(end_date));
          annotations.push(
            this.createLineAnnotation(
              end_date,
              translations && translations['en'] ? translations['en'].title : 'Transition'
            )
          );
        }
      });
    }

    return {
      showlegend: true,
      barmode: 'overlay',
      font: { family: 'Font', size: 10, color: '#1F273C' },
      hovermode: 'x unified',
      legend: { orientation: 'h', y: -0.1 },
      margin: { l: 28, r: 40, t: 0, b: 25 },
      yaxis: { side: 'left', fixedrange: true },
      yaxis2: { overlaying: 'y', side: 'right', fixedrange: true, showgrid: false },
      dragmode: 'zoom',
      shapes,
      annotations
    };
  }

  generateLayout(
    chartType: Plotly.PlotType,
    traces: Partial<Plotly.PlotData>[],
    statisticData: any
  ): Partial<Plotly.Layout> {
    const baseLayout: Partial<Plotly.Layout> = {
      barmode: traces.length > 1 ? 'group' : undefined,
      showlegend: statisticData !== 'line' || traces.length > 1,
      font: { family: 'Font', size: 10, color: '#1F273C' },
      hovermode: 'x unified' as 'x unified',
      hoverlabel: { bgcolor: '#ffffff', bordercolor: '#1F273C' },
      legend: { orientation: 'h', y: -0.1 },
      margin: { l: 28, r: 0, t: 0, b: 25 },
      yaxis: { fixedrange: true },
      dragmode: 'zoom'
    };

    if (statisticData === 'horizontalBar') {
      baseLayout.margin = { l: 115, r: 0, t: 0, b: 25 };
      baseLayout.yaxis = {
        automargin: true,
        fixedrange: true,
        tickpadding: 200,
        ticks: 'outside'
      } as any;
    }

    if (chartType === 'scatter3d') {
      baseLayout.scene = {
        xaxis: { title: 'X Axis' },
        yaxis: { title: 'Y Axis' },
        zaxis: { title: 'Z Axis' },
        camera: { eye: { x: 1.25, y: 1.25, z: 1.25 } }
      };
      baseLayout.dragmode = 'orbit';
    }
    return baseLayout;
  }

  private createVerticalLineShape(endDate: string): Partial<Plotly.Shape> {
    return {
      type: 'line',
      x0: endDate,
      x1: endDate,
      y0: 0,
      y1: 1,
      xref: 'x',
      yref: 'paper',
      line: {
        color: PlotlyService.DASHED_COLOR,
        width: 1,
        dash: 'dash'
      }
    };
  }

  private createLineAnnotation(endDate: string, title: string): Partial<Plotly.Annotations> {
    return {
      x: endDate,
      y: 1,
      xref: 'x',
      yref: 'paper',
      text: title,
      font: { color: PlotlyService.DASHED_COLOR, size: 10 },
      showarrow: false,
      bgcolor: 'white',
      bordercolor: 'white',
      borderwidth: 1,
      xanchor: 'center',
      yanchor: 'top'
    };
  }

  generateAnnotationsForNA(traces: Partial<Plotly.PlotData>[]): any[] {
    const annotations: any[] = [];
    traces.forEach(trace => {
      if ((trace.type === 'bar' || trace.type === 'scatter') && Array.isArray(trace.text)) {
        trace.text.forEach((text: string, pointIndex: number) => {
          if (text === 'N/A' && trace.x && trace.x[pointIndex] !== undefined) {
            annotations.push({
              x: trace.x[pointIndex],
              y: trace.y ? trace.y[pointIndex] : 0,
              text: 'N/A',
              showarrow: false,
              font: { size: 12 },
              xanchor: 'center',
              yanchor: 'bottom'
            });
          }
        });
      }
    });
    return annotations;
  }

  createCombinedLineBarTraces(
    chartData: { data: { date: string; value: number }[]; name: string }[],
    verticalLineDates?: { start_date: string; end_date: string; has_dashed_line: boolean; translations?: any }[]
  ): Partial<Plotly.PlotData>[] {
    // Berechne die Renditen für den Bar-Trace (Returns)
    const returns = chartData[0].data.map((point, index, array) => {
      if (index === 0) return 0;
      return ((point.value - array[index - 1].value) / array[index - 1].value) * 100;
    });

    // Erstelle für jeden Datenpunkt eine Farbe:
    // Falls der Punkt in einer verticalLineDates-Periode liegt, verwende die hervorgehobene Farbe, sonst Standardfarbe.
    const barColors = chartData[0].data.map(point => {
      let inPeriod = false;
      if (verticalLineDates && verticalLineDates.length) {
        for (const { start_date, end_date } of verticalLineDates) {
          if (
            new Date(point.date).getTime() >= new Date(start_date).getTime() &&
            new Date(point.date).getTime() < new Date(end_date).getTime()
          ) {
            inPeriod = true;
            break;
          }
        }
      }
      return inPeriod ? '#F4F2F1' : '#999BBB';
    });

    const barTrace: Partial<Plotly.PlotData> = {
      x: chartData[0].data.map(point => point.date),
      y: returns,
      type: 'bar',
      name: 'Return',
      marker: { color: barColors },
      yaxis: 'y'
    };

    let lineTraces: Partial<Plotly.PlotData>[] = [];

    if (verticalLineDates && verticalLineDates.length) {
      verticalLineDates.forEach(vld => {
        const { start_date, end_date, has_dashed_line } = vld;
        if (has_dashed_line) {
          // Unterteile die Price-Daten in Segmente: before, during und after
          const { before, during, after } = this.filterDataPoints(chartData[0].data, start_date, end_date);

          // Sicherstellen, dass der erste Datenpunkt in "during" enthalten ist
          const firstPoint = chartData[0].data[0];
          if (!during.find(point => point.date === firstPoint.date)) {
            during.unshift(firstPoint);
            const idx = before.findIndex(point => point.date === firstPoint.date);
            if (idx !== -1) {
              before.splice(idx, 1);
            }
          }

          // Bestimme den Übergangspunkt: Wähle den Datenpunkt (aus "during" oder "after"),
          // der dem end_date am nächsten liegt.
          let candidateDuring = during.length ? during[during.length - 1] : null;
          let candidateAfter = after.length ? after[0] : null;
          let transitionPoint: { date: string; value: number } | null = null;
          if (candidateDuring && candidateAfter) {
            const diffDuring = Math.abs(new Date(candidateDuring.date).getTime() - new Date(end_date).getTime());
            const diffAfter = Math.abs(new Date(candidateAfter.date).getTime() - new Date(end_date).getTime());
            transitionPoint = diffDuring <= diffAfter ? candidateDuring : candidateAfter;
          } else {
            transitionPoint = candidateDuring || candidateAfter;
          }
          // Verwende end_date falls kein passender Datenpunkt gefunden wird
          const rawEndDate = transitionPoint ? transitionPoint.date : end_date;
          // Formatiere end_date in das Format "YYYY-MM-DD" – so wie es auch in den x-Achsen der Traces genutzt wird.
          const localEndDate = new Date(rawEndDate).toISOString().split('T')[0];
          // Aktualisiere verticalLineDates, sodass auch die Annotation den korrekten Punkt verwendet
          vld.end_date = localEndDate;

          // Füge einen synthetischen Punkt hinzu, falls "during" nicht mit localEndDate endet.
          let syntheticPoint: { date: string; value: number } | null = null;
          if (!during.length || during[during.length - 1].date.split('T')[0] !== localEndDate) {
            const lastValue = during.length ? during[during.length - 1].value : (before.length ? before[before.length - 1].value : 0);
            syntheticPoint = { date: localEndDate, value: lastValue };
            during.push(syntheticPoint);
          } else {
            syntheticPoint = { date: during[during.length - 1].date.split('T')[0], value: during[during.length - 1].value };
          }
          // Sicherstellen, dass "after" mit localEndDate beginnt.
          if (after.length) {
            if (after[0].date.split('T')[0] !== localEndDate) {
              after.unshift(syntheticPoint);
            }
          }

          // Erzeuge Trace für "before" (durchgehende Linie)
          if (before.length) {
            const traceBefore = this.createTrace(before, 'scatter', '#1F273C', 'solid', chartData[0].name, true);
            traceBefore.yaxis = 'y2';
            lineTraces.push(traceBefore);
          }
          // Erzeuge Trace für "during" (gestrichelte Linie)
          if (during.length) {
            const traceDuring = this.createTrace(during, 'scatter', PlotlyService.DASHED_COLOR, 'dot');
            traceDuring.yaxis = 'y2';
            lineTraces.push(traceDuring);
          }
          // Erzeuge Trace für "after" (durchgehende Linie)
          if (after.length) {
            const traceAfter = this.createTrace(after, 'scatter', '#1F273C', 'solid');
            traceAfter.yaxis = 'y2';
            lineTraces.push(traceAfter);
          }
        }
      });
    } else {
      // Falls keine verticalLineDates angegeben sind: Erzeuge einen vollständigen Line-Trace auf y2.
      const fullLineTrace = this.createTrace(chartData[0].data, 'scatter', '#1F273C', 'solid', chartData[0].name, true);
      fullLineTrace.yaxis = 'y2';
      lineTraces.push(fullLineTrace);
    }

    return [barTrace, ...lineTraces];
  }







  addEventListeners(chartId: string, doubleClickCallback: () => void, zoomCallback: (xRange: [string, string]) => void): void {
    const chartElement = document.getElementById(chartId);
    this.myPlot = new ElementRef(chartElement);

    if (!chartElement) return;

    this.myPlot.nativeElement.on('plotly_relayout', (eventData: any) => {
      const xRange: [string, string] = [eventData['xaxis.range[0]'], eventData['xaxis.range[1]']];
      zoomCallback(xRange);
    });

    this.myPlot.nativeElement.on('plotly_doubleclick', doubleClickCallback);
  }
}
